/**
 * CrownFellowCelebration
 *
 * Mounts on the Certificate page when the participant has reached Crown Fellow
 * (all 7 tracks fully complete). Fires gold confetti and optionally plays a
 * regal fanfare. Plays exactly ONCE per browser per user — tracked in
 * localStorage so it doesn't re-fire on every revisit.
 */
import { useEffect, useRef, useState } from "react";
import confetti from "canvas-confetti";
import { Volume2, VolumeX } from "lucide-react";

// Public-domain trumpet fanfare. Hosted on archive.org — short, regal, ~3s.
// If this CDN ever goes away we can swap to a self-hosted file in /public.
const FANFARE_URL =
  "https://upload.wikimedia.org/wikipedia/commons/3/3b/Trumpet_fanfare.ogg";

function fireConfettiBurst() {
  const duration = 2500;
  const end = Date.now() + duration;
  const colors = ["#C9A961", "#F4D03F", "#FFFFFF", "#8C6A1A"];

  (function frame() {
    confetti({
      particleCount: 4,
      angle: 60,
      spread: 70,
      origin: { x: 0, y: 0.7 },
      colors,
    });
    confetti({
      particleCount: 4,
      angle: 120,
      spread: 70,
      origin: { x: 1, y: 0.7 },
      colors,
    });
    if (Date.now() < end) requestAnimationFrame(frame);
  })();

  // Big finale burst from centre
  setTimeout(() => {
    confetti({
      particleCount: 180,
      spread: 110,
      startVelocity: 45,
      origin: { y: 0.55 },
      colors,
    });
  }, 600);
}

export default function CrownFellowCelebration({ userId, enabled }) {
  const audioRef = useRef(null);
  const [muted, setMuted] = useState(false);
  const storageKey = `tcpi-fellow-celebrated-${userId || "anon"}`;
  const [hasFired, setHasFired] = useState(true); // default true so nothing fires before effect decides

  useEffect(() => {
    if (!enabled) return;
    let alreadyFired = false;
    try {
      alreadyFired = window.localStorage.getItem(storageKey) === "1";
    } catch (err) {
      console.warn("celebration storage check failed:", err?.message);
    }
    if (alreadyFired) return;
    setHasFired(false);
    // Small delay so the certificate has time to render visually
    const t = setTimeout(() => {
      fireConfettiBurst();
      try {
        window.localStorage.setItem(storageKey, "1");
      } catch (err) {
        console.warn("celebration storage write failed:", err?.message);
      }
    }, 500);
    return () => clearTimeout(t);
  }, [enabled, storageKey]);

  // Try to play audio after confetti starts. Browsers may block autoplay —
  // in that case the floating mute/unmute button lets the user opt in.
  useEffect(() => {
    if (!enabled || hasFired || muted) return;
    const a = audioRef.current;
    if (!a) return;
    a.volume = 0.45;
    const playPromise = a.play();
    if (playPromise?.catch) {
      playPromise.catch(() => {
        // Autoplay blocked — user can click the volume button to unblock.
      });
    }
  }, [enabled, hasFired, muted]);

  // Manual replay — fire confetti + audio again from the floating button
  const replay = () => {
    fireConfettiBurst();
    const a = audioRef.current;
    if (a && !muted) {
      a.currentTime = 0;
      a.volume = 0.45;
      a.play().catch(() => {});
    }
  };

  if (!enabled) return null;

  return (
    <>
      <audio
        ref={audioRef}
        src={FANFARE_URL}
        preload="auto"
        data-testid="fellow-fanfare-audio"
      />
      <div
        className="fixed bottom-6 right-6 z-40 flex items-center gap-2"
        data-testid="fellow-celebration-controls"
      >
        <button
          onClick={replay}
          className="px-4 py-2 rounded-full bg-[#C9A961] text-[#0A0908] font-semibold text-sm shadow-lg hover:bg-[#f4d03f] transition"
          data-testid="fellow-replay-btn"
          title="Celebrate again"
        >
          Celebrate again
        </button>
        <button
          onClick={() => {
            const next = !muted;
            setMuted(next);
            const a = audioRef.current;
            if (a) a.muted = next;
          }}
          className="p-2 rounded-full bg-[#1A1816] border border-[#C9A961]/40 text-[#C9A961] hover:bg-[#C9A961]/10 transition"
          aria-label={muted ? "Unmute fanfare" : "Mute fanfare"}
          data-testid="fellow-mute-btn"
        >
          {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        </button>
      </div>
    </>
  );
}
